import type {
    BookingSessionType,
    ScheduledSession,
    ScheduleSubject,
    SubjectKind,
    UpcomingSubjectFilter,
} from "./types";

/**
 * Which request field a subject travels in.
 *
 * The backend refuses a booking that carries both `patient_id` and
 * `couple_patient_id`, or none of the subjects, so the choice is made once here rather than
 * branched at each call site.
 */
export function subjectPayload(
    subject: ScheduleSubject | UpcomingSubjectFilter
): {
    patient_id?: string;
    couple_patient_id?: string;
    group_patient_id?: string;
    participant_id?: string;
} {
    if (subject.kind === "couple") return { couple_patient_id: subject.id };
    if (subject.kind === "group") return { group_patient_id: subject.id };
    if (subject.kind === "participant") return { participant_id: subject.id };
    return { patient_id: subject.id };
}

/** The kind of session booked with a subject — which decides how long its slots are. */
export function sessionTypeOf(kind: SubjectKind | null | undefined): BookingSessionType {
    if (kind === "couple") return "couple";
    if (kind === "group") return "group";
    return "individual";
}

/** The subject a stored session belongs to, or null when it names neither. */
export function subjectOf(
    session: ScheduledSession
): { kind: SubjectKind; id: string } | null {
    if (session.couple_patient_id) {
        return { kind: "couple", id: session.couple_patient_id };
    }
    if (session.group_patient_id) {
        return { kind: "group", id: session.group_patient_id };
    }
    if (session.patient_id) {
        return { kind: "patient", id: session.patient_id };
    }
    return null;
}

/**
 * The name to show for a session, looked up by whichever subject it carries.
 *
 * A couple booking has no `patient_id`, so a lookup keyed only on that would
 * render blank — the one assumption the couple contract breaks.
 */
export function subjectLabel(
    session: ScheduledSession,
    names: Map<string, string>
): string | undefined {
    const subject = subjectOf(session);
    return subject ? names.get(subject.id) : undefined;
}

/** Whether a session belongs to the given subject. */
export function matchesSubject(
    session: ScheduledSession,
    subject: UpcomingSubjectFilter
): boolean {
    const own = subjectOf(session);
    return own !== null && own.kind === subject.kind && own.id === subject.id;
}
